<script setup lang="ts">
import { ref, computed, watchEffect } from 'vue'
import { storeToRefs } from 'pinia'
import { useHead } from '@unhead/vue'

import { AssetType } from '@xcel-next/core/enums'

import { useConfigStore } from '../../../stores/config'
import { useImgSrcSet } from '../../../composables/use-img-src-set'
import { useClient } from '../../../utils/client-only'
import { shouldPreloadParagraphImage } from '../../../utils/image-priority'
// Direct import intentional - DO NOT refactor to barrel import ('../../paragraph-types').
// SourceSet.vue > paragraph-types/index.ts > ParagraphTypeImage.vue > SourceSet.vue is a
// circular dependency that causes `resolveComponent` to receive `undefined` during SSR,
// producing a "[Vue warn]: Failed to resolve component: ParagraphTypeAffiliateLink" on the server.
import ParagraphTypeAffiliateLink from '../../paragraph-types/ParagraphTypeAffiliateLink.vue'
import ImageFigure from './Figure.vue'

import type { AffiliateLinkParagraph, ImageParagraph } from '../../../utils/paragraph-schema/paragraphs'
import type { Brand } from '@xcel-next/core/enums'

type ImageProps = NonNullable<ImageParagraph['image']>

const props = withDefaults(defineProps<{
  image: ImageProps,
  brand?: Brand,
  typeCount?: number,
  listOrderPosition?: number,
  isPreview?: boolean,
  target?: ImageProps['target'],
  rel?: ImageProps['rel'],
  imageSrcSetName?: string
}>(), {
  typeCount: 0,
  listOrderPosition: -1,
  isPreview: false
})

const { createImgSrcSetArray } = useImgSrcSet()
const { rsConfig } = storeToRefs(useConfigStore())
const { isClient } = useClient()

const fallbackPlaceholderSource = ref<string>()
const fallbackPlaceholderSourceWidth = ref(0)

const hasOverlay = computed(() => !!(props.image.overlay?.url && props.image.overlay?.title))
// Render after mount to keep SSR and initial client DOM identical.
const showOverlayAffiliateLink = computed(() => isClient.value && hasOverlay.value)
// image.overlay is a reduced subset of the affiliate-link paragraph shape (no subtitle/highlighted)
const overlayAffiliateLink = computed(() => props.image.overlay as AffiliateLinkParagraph['affiliateLink'])
const imgSrcSetName = computed(() => props.imageSrcSetName || 'paragraph_' + (props.image.layout ? props.image.layout.size : 'l'))

const getLoadingValue = (): 'lazy' | 'eager' =>
  (!props.isPreview && props.listOrderPosition >= (rsConfig.value.imageLazyLoading?.aboveTheFoldParagraphCount[AssetType.Article] ?? Infinity)) ? 'lazy' : 'eager'

const stripDoubleQuotes = (inputString?: string): string | undefined =>
  inputString?.length ? inputString.replace(/["]+/g, '') : undefined

const webpSrcSet = computed(() => createImgSrcSetArray(props.image, props.brand ?? '', imgSrcSetName.value, 'webp'))

watchEffect(() => {
  for (const singleSrc of webpSrcSet.value) {
    const widthMatch = singleSrc.src.match(/w=([0-9]+)/g)
    if (!widthMatch) continue
    const imageWidth = parseInt(widthMatch[0].substring(2))
    if (imageWidth > fallbackPlaceholderSourceWidth.value) {
      fallbackPlaceholderSourceWidth.value = imageWidth
      fallbackPlaceholderSource.value = singleSrc.src.replace('.jpeg', '.webp')
    }
  }
})

const setMediaQuery = (viewport: number | string): string => '(max-width: ' + viewport + 'px)'

const setMediaQueryPreload = (minViewport: number | string, maxViewport: number | string): string =>
  '(min-width: ' + minViewport + '.1px) and (max-width: ' + maxViewport + 'px)'

// add preload link for lead image srcset - webp only
useHead(computed(() => {
  if (!shouldPreloadParagraphImage(props.typeCount, props.listOrderPosition)) return {}
  const sourceArray = webpSrcSet.value
  return {
    link: sourceArray.map((singleSrc, index) => ({
      rel: 'preload',
      href: singleSrc.src,
      as: 'image',
      fetchpriority: 'high',
      media: setMediaQueryPreload(index > 0 ? sourceArray[index - 1].viewportWidth : 0, singleSrc.viewportWidth),
      vmid: 'img-preload-' + singleSrc.viewportWidth
    }))
  }
}))
</script>

<template>
  <ImageFigure :image="image"
               :target="target"
               :rel="rel"
               :is-preview="isPreview"
               :list-order-position="listOrderPosition">
    <template #sourceSet>
      <picture>
        <source v-for="(singleSrc, index) in webpSrcSet"
                :key="index + 'webp'"
                type="image/webp"
                :media="setMediaQuery(singleSrc.viewportWidth)"
                :srcset="singleSrc.src">
        <img :src="fallbackPlaceholderSource"
             :loading="getLoadingValue()"
             :title="stripDoubleQuotes(image.imageTitle)"
             :alt="stripDoubleQuotes(image.imageAltText)"
             :width="image.width"
             :height="image.height">
        <ParagraphTypeAffiliateLink v-if="showOverlayAffiliateLink"
                                    :affiliate-link="overlayAffiliateLink"
                                    :is-image-overlay="true" />
      </picture>
    </template>
  </ImageFigure>
</template>
